import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import Button from '../components/ui/Button';
import NetworkVisual from '../components/visuals/NetworkVisual';
import Comparison from '../components/sections/Comparison';

const GoogleMapsService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const includes = [
    {
      title: "GBP Optimization",
      desc: "Complete optimization of your Google Business Profile for maximum visibility.",
      icon: FaIcons.FaGoogle
    },
    {
      title: "Local Keyword Targeting",
      desc: "Targeting high-intent terms that local customers are actually searching for.",
      icon: FiIcons.FiTarget
    },
    {
      title: "Listing Improvements",
      desc: "Refining business information to satisfy Google's proximity algorithm.",
      icon: FiIcons.FiEdit3
    },
    {
      title: "Category Alignment",
      desc: "Ensuring business is perfectly categorized for map pack appearances.",
      icon: FiIcons.FiLayers
    },
    {
      title: "Ongoing Performance",
      desc: "Continuous monitoring to maintain and improve local standings.",
      icon: FiIcons.FiActivity
    }
  ];

  return (
    <div className="bg-[#0B0F19] min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-40 pb-16 overflow-hidden border-b border-white/5">
        <NetworkVisual />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center flex flex-col items-center">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-7xl font-extrabold text-white mb-6 leading-tight tracking-tight"
            >
              Google Maps SEO & <br/>
              <span className="text-indigo-500">Local Ranking</span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-slate-400 mb-10 leading-relaxed max-w-2xl mx-auto"
            >
              Improve your visibility on Google Maps, attract more local customers, and grow your business with structured local SEO campaigns.
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-6 w-full sm:w-auto"
            >
              <Button href="#/contact" variant="primary" className="!from-indigo-600 !to-blue-600 !px-12 !py-5 text-xl font-bold shadow-[0_0_40px_rgba(79,70,229,0.4)] w-full sm:w-auto">
                Start Improving Visibility
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Dashboard Image Section - Increased Padding to prevent clipping */}
      <section className="relative pt-24 pb-24 bg-[#0B0F19] overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="flex justify-center relative z-20"
          >
            <img 
              src="https://media-manager-c.questera.ai/greta-media/22508bb601940b4e20f7d160303c50efd71c7583e6bf992668d180d798243f38c2166642d2b71e0a7892275161f0f512/images/aW1hZ2Uvd2VicA==/12cf4b095a4050fcb89236d99b9674c1.webp" 
              alt="Google Maps SEO Ranking Dashboard"
              className="w-full max-w-5xl h-auto object-contain drop-shadow-[0_20px_50px_rgba(79,70,229,0.2)]"
            />
          </motion.div>
        </div>
      </section>

      {/* Comparison Section */}
      <Comparison />

      {/* Value Prop Section - Centralized */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            
            <h2 className="text-3xl md:text-6xl font-bold text-white mb-8">Get Found Where Customers Are Searching</h2>
            <div className="prose prose-invert text-slate-400 space-y-8 mx-auto mb-24">
              <p className="text-2xl leading-relaxed max-w-3xl mx-auto">
                When users search for services near them, Google Maps results are often the first thing they see. Ranking higher in these results can directly impact calls, website visits, and new customers.
              </p>
              <p className="text-2xl leading-relaxed max-w-3xl mx-auto">
                PRBoost helps businesses and agencies optimize and position their listings to improve visibility and performance on Google Maps.
              </p>
            </div>

            {/* What This Service Includes */}
            <div className="mb-32">
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-12 inline-block underline underline-offset-[12px] decoration-indigo-500/50">
                What This Service Includes
              </h2>
              <div className="flex flex-wrap justify-center gap-8 mt-4">
                {includes.map((item, idx) => (
                  <div key={idx} className="w-full sm:w-[calc(50%-16px)] md:w-[calc(33.333%-22px)] p-10 rounded-3xl glass-card border-white/5 hover:border-indigo-500/20 transition-all group flex flex-col items-center text-center">
                    <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center mb-8 text-indigo-400 group-hover:scale-110 transition-transform">
                      <SafeIcon icon={item.icon} className="text-3xl" />
                    </div>
                    <h4 className="text-white font-bold text-xl mb-4">{item.title}</h4>
                    <p className="text-slate-400 text-lg leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Why Rankings Matter */}
            <div className="space-y-12 max-w-4xl mx-auto">
              <div className="glass-card rounded-[3rem] p-12 lg:p-20 border-indigo-500/20 bg-gradient-to-br from-slate-900/90 to-indigo-900/10 text-center">
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-8">Why Rankings Matter</h2>
                <p className="text-slate-400 mb-12 leading-relaxed text-2xl max-w-2xl mx-auto">
                  Google Maps is one of the most important channels for local businesses. Appearing in top positions can significantly increase exposure and customer engagement.
                </p>
                <div className="flex justify-center">
                  <ul className="space-y-6 text-left inline-flex flex-col">
                    {[
                      "Increase calls and inquiries",
                      "Drive more local traffic",
                      "Improve visibility in your service area",
                      "Strengthen your local presence",
                      "Support your overall marketing efforts"
                    ].map((benefit, i) => (
                      <li key={i} className="flex items-center gap-5 text-slate-300 text-xl">
                        <SafeIcon icon={FiIcons.FiCheckCircle} className="text-indigo-400 text-2xl flex-shrink-0" />
                        <span className="font-medium">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Ongoing Optimization */}
              <div className="p-12 lg:p-20 rounded-[3rem] bg-slate-900 border border-white/5 relative overflow-hidden text-center">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-5 pointer-events-none">
                  <SafeIcon icon={FiIcons.FiMap} className="text-[16rem] text-indigo-500" />
                </div>
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-8 relative z-10">Ongoing Optimization</h2>
                <p className="text-slate-400 text-2xl leading-relaxed mb-12 relative z-10 max-w-3xl mx-auto">
                  Local rankings are influenced by multiple factors and require consistent updates. Our approach focuses on improving your presence over time.
                </p>
                <div className="flex items-center justify-center gap-4 text-indigo-400 text-base font-bold uppercase tracking-[0.2em] relative z-10">
                  <SafeIcon icon={FiIcons.FiRefreshCw} className="animate-spin-slow text-2xl" /> Data-Driven Refinement
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 relative overflow-hidden bg-[#070b14]">
        <div className="absolute inset-0 bg-indigo-600/5 blur-[120px] rounded-full"></div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-7xl font-extrabold text-white mb-8 tracking-tight">Start Improving Your <br/><span className="text-indigo-500">Local Visibility</span></h2>
          <p className="text-2xl text-slate-400 mb-16 max-w-2xl mx-auto leading-relaxed">
            Ranking higher on Google Maps can directly impact your business growth. Reach more customers and increase your local presence today.
          </p>
          <div className="flex justify-center">
            <Button href="#/contact" variant="primary" className="!from-indigo-600 !to-blue-600 !px-16 !py-6 text-2xl font-bold shadow-[0_0_60px_rgba(79,70,229,0.3)]">
              Contact us
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GoogleMapsService;
